import React, { useState, useEffect } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || '';

const GROUND_OPTIONS = [
  { value: '0', label: 'Hard ground', desc: 'Pavement, water, concrete' },
  { value: '0.5', label: 'Mixed', desc: 'Typical project site' },
  { value: '1', label: 'Soft ground', desc: 'Grass, farmland, soil' },
];

const mToFt = (m) => (m * 3.28084).toFixed(1);
const cToF = (c) => (c * 9 / 5 + 32).toFixed(0);

function CalculatorForm({ onCalculate, loading }) {
  const [equipment, setEquipment] = useState(null);
  const [activePreset, setActivePreset] = useState('tesla_megapack');

  const [form, setForm] = useState({
    sound_power_level: 80,
    distance: 300,
    source_height: 2.5,
    receiver_height: 1.5,
    ground_factor: 0.5,
    temperature_c: 20,
    relative_humidity: 70,
    meteorological_correction: 0,
    noise_limit: 45,
    limit_standard: 'who_night',
  });

  useEffect(() => {
    fetch(`${API_BASE}/api/equipment`)
      .then((r) => r.json())
      .then(setEquipment)
      .catch(() => {});
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: parseFloat(value) || 0 }));
    if (name === 'sound_power_level' || name === 'source_height') {
      setActivePreset(null);
    }
  };

  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: parseFloat(value) }));
  };

  const handleLimitChange = (e) => {
    const val = e.target.value;
    setForm((prev) => ({ ...prev, limit_standard: val }));
    if (val === 'custom') return;
    const mapping = {
      who_day: 55, who_evening: 50, who_night: 45,
      us_rural_day: 55, us_rural_night: 50,
      us_suburban_day: 60, us_suburban_night: 50,
      ifc_day: 55, ifc_night: 45,
    };
    if (mapping[val] !== undefined) {
      setForm((prev) => ({ ...prev, noise_limit: mapping[val] }));
    }
  };

  const applyPreset = (preset) => {
    setActivePreset(preset.id);
    setForm((prev) => ({
      ...prev,
      sound_power_level: preset.sound_power_level,
      source_height: preset.source_height,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const { noise_limit, limit_standard, ...calcData } = form;
    onCalculate({ ...calcData, _noise_limit: noise_limit });
  };

  const quickPresets = equipment
    ? equipment.categories
        .map((cat) => {
          const typical = cat.presets.find((p) => p.id.includes('typical') || p.id.includes('megapack')) || cat.presets[0];
          return { ...typical, category: cat.name };
        })
    : [];

  return (
    <form onSubmit={handleSubmit}>
      {/* Source Equipment */}
      <div className="card">
        <div className="card-header">
          <div className="card-header-icon source">S</div>
          <h2>Source Equipment</h2>
        </div>
        <div className="card-body">
          {quickPresets.length > 0 && (
            <>
              <div className="preset-label">Equipment Presets</div>
              <div className="preset-grid">
                {quickPresets.map((p) => (
                  <button
                    type="button"
                    key={p.id}
                    className={`preset-btn ${activePreset === p.id ? 'active' : ''}`}
                    onClick={() => applyPreset(p)}
                  >
                    {p.category}
                    <span className="preset-lw">LW = {p.sound_power_level} dBA</span>
                  </button>
                ))}
              </div>
              <hr className="section-divider" />
            </>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>
                Sound Power Level <span className="unit">(LW, dBA)</span>
              </label>
              <input
                type="number"
                name="sound_power_level"
                value={form.sound_power_level}
                onChange={handleChange}
                min="0" max="150" step="1"
                required
              />
              <div className="form-help">Manufacturer spec or estimated value</div>
            </div>
            <div className="form-group">
              <label>
                Source Height <span className="unit">(m)</span>
              </label>
              <input
                type="number"
                name="source_height"
                value={form.source_height}
                onChange={handleChange}
                min="0" max="100" step="0.5"
                required
              />
              <div className="form-help">{mToFt(form.source_height)} ft</div>
            </div>
          </div>

          <div className="form-group">
            <label>
              Distance to Receiver <span className="unit">(m)</span>
            </label>
            <input
              type="number"
              name="distance"
              value={form.distance}
              onChange={handleChange}
              min="1" max="10000" step="1"
              required
            />
            <div className="form-help">{mToFt(form.distance)} ft &mdash; horizontal distance from nearest equipment to receiver</div>
          </div>
        </div>
      </div>

      {/* Site Parameters */}
      <div className="card">
        <div className="card-header">
          <div className="card-header-icon site">P</div>
          <h2>Site Parameters</h2>
        </div>
        <div className="card-body">
          <div className="form-row">
            <div className="form-group">
              <label>Receiver Height</label>
              <select name="receiver_height" value={form.receiver_height} onChange={handleSelectChange}>
                <option value="1.5">1.5 m / {mToFt(1.5)} ft (ground floor)</option>
                <option value="4.0">4.0 m / {mToFt(4.0)} ft (upper floor)</option>
              </select>
            </div>
            <div className="form-group">
              <label>Ground Type</label>
              <select name="ground_factor" value={form.ground_factor} onChange={handleSelectChange}>
                {GROUND_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label} (G={opt.value})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row-3">
            <div className="form-group">
              <label>Temperature <span className="unit">({'\u00B0'}C)</span></label>
              <input
                type="number" name="temperature_c"
                value={form.temperature_c} onChange={handleChange}
                min="-40" max="60" step="1"
              />
              <div className="form-help">{cToF(form.temperature_c)}{'\u00B0'}F</div>
            </div>
            <div className="form-group">
              <label>Humidity <span className="unit">(%)</span></label>
              <input
                type="number" name="relative_humidity"
                value={form.relative_humidity} onChange={handleChange}
                min="10" max="100" step="5"
              />
            </div>
            <div className="form-group">
              <label>Meteo Corr. <span className="unit">(C0, dB)</span></label>
              <input
                type="number" name="meteorological_correction"
                value={form.meteorological_correction} onChange={handleChange}
                min="0" max="5" step="0.5"
              />
              <div className="form-help">0=neutral, 5=downwind</div>
            </div>
          </div>
        </div>
      </div>

      {/* Compliance Criteria */}
      <div className="card">
        <div className="card-header">
          <div className="card-header-icon compliance">C</div>
          <h2>Compliance Criteria</h2>
        </div>
        <div className="card-body">
          <div className="form-group">
            <label>Standard</label>
            <select name="limit_standard" value={form.limit_standard} onChange={handleLimitChange}>
              <optgroup label="WHO Guidelines">
                <option value="who_night">WHO Nighttime &mdash; 45 dBA</option>
                <option value="who_evening">WHO Evening &mdash; 50 dBA</option>
                <option value="who_day">WHO Daytime &mdash; 55 dBA</option>
              </optgroup>
              <optgroup label="Typical US Rural">
                <option value="us_rural_night">US Rural Night &mdash; 50 dBA</option>
                <option value="us_rural_day">US Rural Day &mdash; 55 dBA</option>
              </optgroup>
              <optgroup label="Typical US Suburban">
                <option value="us_suburban_night">US Suburban Night &mdash; 50 dBA</option>
                <option value="us_suburban_day">US Suburban Day &mdash; 60 dBA</option>
              </optgroup>
              <optgroup label="IFC / World Bank">
                <option value="ifc_night">IFC Nighttime &mdash; 45 dBA</option>
                <option value="ifc_day">IFC Daytime &mdash; 55 dBA</option>
              </optgroup>
              <option value="custom">Custom Limit</option>
            </select>
          </div>
          <div className="form-group">
            <label>Noise Limit <span className="unit">(dBA)</span></label>
            <input
              type="number" name="noise_limit"
              value={form.noise_limit} onChange={handleChange}
              min="0" max="150" step="1"
            />
          </div>
        </div>
      </div>

      <button type="submit" className="btn-calculate" disabled={loading}>
        {loading ? 'Calculating...' : 'Calculate Noise Level'}
      </button>
    </form>
  );
}

export default CalculatorForm;
